import { useEffect, useRef } from "react";
import { LOCALES, localeLabels, localeShortLabels, useI18n } from "~/i18n";
import { Icon } from "./icon";
import "./language-picker.css";

/** Same persistent preference as the landing, available without changing route. */
export function LanguagePicker({
  compact = false,
  className = "",
}: {
  compact?: boolean;
  className?: string;
}) {
  const { t, locale, setLocale } = useI18n();
  const ref = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    const picker = ref.current;
    if (!picker) return;
    function closeOutside(event: PointerEvent) {
      if (picker?.open && event.target instanceof Node && !picker.contains(event.target)) {
        picker.open = false;
      }
    }
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape" && picker?.open) {
        event.stopPropagation();
        picker.open = false;
        picker.querySelector("summary")?.focus();
      }
    }
    document.addEventListener("pointerdown", closeOutside);
    picker.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      picker.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  return (
    <details
      ref={ref}
      className={`language-picker${compact ? " language-picker--compact" : ""} ${className}`}
      onBlur={(event) => {
        if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) {
          event.currentTarget.open = false;
        }
      }}
    >
      <summary aria-label={`${t("common.language")}: ${localeLabels[locale]}`}>
        <Icon name="globe" size={18} />
        <span className="language-picker__name">{localeLabels[locale]}</span>
        <span className="language-picker__short">{localeShortLabels[locale]}</span>
        <Icon name="chevron" size={16} />
      </summary>
      <div className="language-picker__options">
        <p>{t("common.language")}</p>
        {LOCALES.map((option) => (
          <button
            type="button"
            key={option}
            lang={option}
            aria-pressed={locale === option}
            onClick={() => {
              setLocale(option);
              if (ref.current) {
                ref.current.open = false;
                ref.current.querySelector("summary")?.focus();
              }
            }}
          >
            <span className="language-picker__code" aria-hidden="true">
              {localeShortLabels[option]}
            </span>
            <span>{localeLabels[option]}</span>
            {locale === option ? <Icon name="check" size={16} /> : null}
          </button>
        ))}
      </div>
    </details>
  );
}
